// src/hooks/charts/useCorrelationData.js
import { useMemo } from "react";
import useFetchGraphData from "../useFetchGraphData";

const median = (arr) => {
  const mid = Math.floor(arr.length / 2);
  const nums = [...arr].sort((a, b) => a - b);
  return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};

export default function useCorrelationData(
  { locationids = [], p1, p2 },
  aggregationMethod = "average",
  removeOutliers = false
) {
  const locs = Array.isArray(locationids)
    ? locationids.map((l) => parseInt(l, 10)).filter((n) => !isNaN(n))
    : [];
  const locParam = locs.join(",");

  const {
    data,
    isFetching: isLoading,
    isError,
  } = useFetchGraphData(
    { locationids: locParam, p1, p2 },
    "results-simple/correlation-graph"
  );

  return useMemo(() => {
    const EMPTY = { series: [], xLabel: "", yLabel: "", isLoading, isError };
    if (!Array.isArray(data) || !data.length) return EMPTY;

    const getAggregatedValue = (vals) => {
      switch (aggregationMethod) {
        case "median":
          return median(vals);
        case "first":
          return vals[0];
        default:
          return vals.reduce((a, b) => a + b, 0) / vals.length;
      }
    };

    function processLocation(rows, index) {
      // 1) build daily-aggregated points
      const groupByDate = (paramId) => {
        const map = {};
        rows.forEach((r) => {
          if (+r.parameterid !== paramId) return;
          const d = r.rdate.slice(0, 10);
          map[d] = map[d] || [];
          map[d].push(+r.rvalue_converted);
        });
        return Object.fromEntries(
          Object.entries(map).map(([d, vals]) => [d, getAggregatedValue(vals)])
        );
      };

      const xAvg = groupByDate(p1);
      const yAvg = groupByDate(p2);
      const dates = Object.keys(xAvg).filter((d) => d in yAvg);
      if (!dates.length) return null;

      // 2) build full point objects (with raw arrays attached)
      const allPoints = dates.map((d) => ({
        x: xAvg[d],
        y: yAvg[d],
        date: d,
        unit1: rows.find((r) => +r.parameterid === p1 && r.rdate.startsWith(d))
          ?.unit_name,
        unit2: rows.find((r) => +r.parameterid === p2 && r.rdate.startsWith(d))
          ?.unit_name,
      }));

      // 3) initial regression to detect outliers
      const n0 = allPoints.length;
      const sx0 = allPoints.reduce((s, p) => s + p.x, 0);
      const sy0 = allPoints.reduce((s, p) => s + p.y, 0);
      const sxy0 = allPoints.reduce((s, p) => s + p.x * p.y, 0);
      const sx20 = allPoints.reduce((s, p) => s + p.x * p.x, 0);

      const b1_0 = (n0 * sxy0 - sx0 * sy0) / (n0 * sx20 - sx0 * sx0);
      const b0_0 = (sy0 - b1_0 * sx0) / n0;

      // 4) compute residuals & flag outliers >2σ
      const residuals = allPoints.map((p) => p.y - (b1_0 * p.x + b0_0));
      const σ = Math.sqrt(residuals.reduce((a, r) => a + r ** 2, 0) / (n0 - 2));
      const outliers = allPoints.filter(
        (p, i) => Math.abs(residuals[i]) > 2 * σ
      );

      // 5) decide which points to actually fit
      const fitPoints = removeOutliers
        ? allPoints.filter((p) => !outliers.includes(p))
        : allPoints;

      const n = fitPoints.length;
      const sx = fitPoints.reduce((s, p) => s + p.x, 0);
      const sy = fitPoints.reduce((s, p) => s + p.y, 0);
      const sxy = fitPoints.reduce((s, p) => s + p.x * p.y, 0);
      const sx2 = fitPoints.reduce((s, p) => s + p.x * p.x, 0);
      const sy2 = fitPoints.reduce((s, p) => s + p.y * p.y, 0);

      const slope = (n * sxy - sx * sy) / (n * sx2 - sx * sx);
      const intercept = (sy - slope * sx) / n;
      const pearson =
        (n * sxy - sx * sy) /
        Math.sqrt((n * sx2 - sx * sx) * (n * sy2 - sy * sy));
      const rSquared = pearson ** 2;

      // 6) confidence bands, as before
      const xs = fitPoints.map((p) => p.x);
      const xMean = sx / n;
      const ssx = xs.reduce((sum, x) => sum + (x - xMean) ** 2, 0);
      const σr = Math.sqrt(residuals.reduce((a, r) => a + r ** 2, 0) / (n - 2));
      const t = 2.0;

      const [minX, maxX] = [Math.min(...xs), Math.max(...xs)];
      const se = (x) => σr * Math.sqrt(1 / n + (x - xMean) ** 2 / ssx);

      const lowerBand = [
        { x: minX, y: slope * minX + intercept - t * se(minX) },
        { x: maxX, y: slope * maxX + intercept - t * se(maxX) },
      ];
      const upperBand = [
        { x: minX, y: slope * minX + intercept + t * se(minX) },
        { x: maxX, y: slope * maxX + intercept + t * se(maxX) },
      ];

      return {
        locationid: rows[0].locationid,
        name: rows[0].location_name,
        points: fitPoints,
        outliers,
        slope,
        intercept,
        pearson,
        rSquared,
        n,
        lowerBand,
        upperBand,
        colorIndex: index,
      };
    }

    const byLocation = data.reduce((acc, r) => {
      (acc[r.locationid] = acc[r.locationid] || []).push(r);
      return acc;
    }, {});

    const series = Object.values(byLocation)
      .map((rows, i) => processLocation(rows, i))
      .filter(Boolean);

    const exampleX = data.find((r) => +r.parameterid === p1);
    const exampleY = data.find((r) => +r.parameterid === p2);

    const xLabel = exampleX
      ? `${exampleX.parameter} (${exampleX.unit_name})`
      : "X";
    const yLabel = exampleY
      ? `${exampleY.parameter} (${exampleY.unit_name})`
      : "Y";

    return { series, xLabel, yLabel, isLoading, isError };
  }, [data, isLoading, isError, p1, p2, aggregationMethod, removeOutliers]);
}
